import { useEffect, useState } from 'react';
import { ChevronDown, CheckCircle2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { OnboardingTimeline, type SectionKey } from '@/components/onboarding/OnboardingTimeline';
import { WelcomeCard } from '@/components/onboarding/WelcomeCard';
import { EnrichmentSurface } from '@/components/onboarding/enrichment/EnrichmentSurface';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@/components/ui/collapsible';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/contexts/AuthContext';
import { useOnboardingState } from '@/contexts/OnboardingContext';
import { deriveBotStatus } from '@/lib/botStatus';

export function BotPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { state, loading, markWelcomeSeen } = useOnboardingState();
  const location = useLocation();
  // Deep-link from the status chip (e.g. "Reconectar" → meta_oauth): opens
  // the collapsed timeline and expands the requested step. Controlled (not
  // defaultOpen) because the chip is also clickable while this page is
  // already mounted — the navigation only changes location.state.
  const expandStep = (location.state as { expandStep?: SectionKey } | null)?.expandStep ?? null;
  const [timelineOpen, setTimelineOpen] = useState(expandStep !== null);
  useEffect(() => {
    if (expandStep !== null) {
      setTimelineOpen(true);
    }
  }, [expandStep, location.key]);

  if (loading) {
    return (
      <div className="space-y-6" data-testid="bot-page-loading">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4" />
      </div>
    );
  }

  const status = deriveBotStatus(state);
  const titleKey = status === 'live' ? 'bot.page.title_live' : 'bot.page.title_setup';
  const subtitleKey = status === 'live' ? 'bot.page.subtitle_live' : 'bot.page.subtitle_setup';
  // F174 FR1 — the enrichment cards edit the same KB profile the wizard
  // writes, so they unlock as soon as that step is done (SETUP or LIVE),
  // not gated behind Instagram/Stripe/go-live. The faded F052 look only
  // applies before the KB step.
  const kbStepCompleted = state?.kb_completed_at != null;

  const firstName = user ? (user.name.split(/\s+/)[0] ?? user.name) : '';

  // FR1 — first-run gate: a brand-new tenant (welcome card not yet dismissed
  // AND the funnel not completed) sees the WelcomeCard INSTEAD of the greeting
  // + timeline. The card absorbs the greeting, so nothing is duplicated.
  const showWelcome =
    state !== null && state.welcome_seen_at === null && state.current_step !== 'completed';
  if (showWelcome) {
    return (
      <div className="py-2">
        <WelcomeCard name={firstName} onStart={markWelcomeSeen} />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Page h1 — greeting */}
      <div className="space-y-1.5">
        <h1 className="text-2xl font-semibold tracking-tight">
          {t('dashboard.greeting', { name: firstName })}
        </h1>
        <p className="text-sm text-muted-foreground">{t('dashboard.subtitle')}</p>
      </div>

      {/* Bot section heading — h2-level, morphs by lifecycle */}
      <div className="space-y-1">
        <h2 className="text-xl font-semibold tracking-tight">{t(titleKey)}</h2>
        <p className="text-sm text-muted-foreground">{t(subtitleKey)}</p>
      </div>

      {status === 'live' ? (
        /* Live: timeline collapsed by default inside a Collapsible */
        <Collapsible open={timelineOpen} onOpenChange={setTimelineOpen}>
          <CollapsibleTrigger
            data-testid="timeline-collapsible-trigger"
            className="flex w-full items-center justify-between gap-2 rounded-lg border px-4 min-h-11 text-sm font-medium transition-colors hover:bg-muted/50"
          >
            <span className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-success" aria-hidden="true" />
              {t('bot.timeline.config_complete')}
            </span>
            <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform duration-200 [[data-state=open]_&]:rotate-180" />
          </CollapsibleTrigger>
          <CollapsibleContent className="pt-4">
            <OnboardingTimeline initialEditStep={expandStep} editRequestId={location.key} />
          </CollapsibleContent>
        </Collapsible>
      ) : (
        /* Setup: timeline rendered inline, no collapsible */
        <OnboardingTimeline />
      )}

      {/* Enrichment cards — always visible; disabled/faded until the KB step
          is done (F174 FR1 — no longer gated on go-live). */}
      <EnrichmentSurface disabled={!kbStepCompleted} />
    </div>
  );
}
